const PREFERRED_LANG = 'pt-BR';

let currentUtterance: SpeechSynthesisUtterance | null = null;
let speakGeneration = 0;

export function isSpeechSynthesisSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

function pickPtBrVoice(): SpeechSynthesisVoice | null {
  const voices = window.speechSynthesis?.getVoices?.() ?? [];
  return (
    voices.find((voice) => voice.lang === PREFERRED_LANG) ??
    voices.find((voice) => voice.lang?.toLowerCase().startsWith('pt')) ??
    null
  );
}

/**
 * Chromium: `cancel()` sozinho não corta o utterance atual.
 * Precisa `pause()` antes e soltar os handlers do utterance vivo.
 */
export function cancelSpeech(): void {
  if (!isSpeechSynthesisSupported()) return;
  speakGeneration += 1;

  const synth = window.speechSynthesis;
  const utterance = currentUtterance;
  currentUtterance = null;
  if (utterance) {
    utterance.onend = null;
    utterance.onerror = null;
  }

  try {
    synth.pause();
  } catch {
    // ignore
  }
  synth.cancel();
}

export function speakPortuguese(
  text: string,
  { onend }: { onend?: () => void } = {}
): boolean {
  if (!isSpeechSynthesisSupported()) return false;

  const clean = String(text ?? '')
    .replace(/[#*_`>~]/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (!clean) {
    onend?.();
    return false;
  }

  cancelSpeech();
  const generation = speakGeneration;
  const utterance = new SpeechSynthesisUtterance(clean);
  utterance.lang = PREFERRED_LANG;
  utterance.rate = 1.02;
  const voice = pickPtBrVoice();
  if (voice) utterance.voice = voice;

  const finish = (natural: boolean) => {
    if (generation !== speakGeneration) return;
    if (currentUtterance === utterance) currentUtterance = null;
    if (natural) onend?.();
  };

  utterance.onend = () => finish(true);
  utterance.onerror = (event) => {
    const code = String(event.error);
    const aborted = code === 'interrupted' || code === 'canceled';
    finish(!aborted);
  };

  currentUtterance = utterance;
  window.speechSynthesis.speak(utterance);
  return true;
}
